/**
 * Reading interactive features from a map click: query the rendered features under the pointer,
 * attach a derived entity type to each, then filter/deduplicate them.
 */
import { MapGeoJSONFeature, MapMouseEvent } from 'shared/map-exports';
import {
  ExternalFeatureLayer,
  FeatureWithEntityType,
  LayerMetadata,
} from 'features/mapping/mapping.types';
import { LOCATION_LABELS_LAYER_ID } from 'features/mapping/styles';
import {
  INTERACTIVE_LAYER_IDS,
  INTERACTIVE_LAYER_IDS_BY_DRAWER_OR_POI,
} from './constants';
import {
  ExternalFeatureLayerMetadata,
  getSimpleStyleClickListenerLayerId,
} from '../components/layers/ExternalFeatureLayer';
import { PHOTO_MARKERS_LAYER_ID } from '../components/layers/PhotoMarkersLayer';
import { isDeckObjectAtMapClick } from '../components/DeckOverlay';

const LAYER_ID_TO_ENTITY_TYPE = new Map<string, string>();
for (const [entityType, layerIds] of Object.entries(
  INTERACTIVE_LAYER_IDS_BY_DRAWER_OR_POI,
)) {
  for (const layerId of layerIds) {
    LAYER_ID_TO_ENTITY_TYPE.set(layerId, entityType);
  }
}

/**
 * Query rendered features constrained to interactive layers,
 * then filter by layer metadata.interactive (default true).
 */
export const queryInteractiveFeatures = (
  event: MapMouseEvent,
  dynamicLayerIds: string[],
  disabledLayerIds: string[],
): MapGeoJSONFeature[] => {
  const map = event.target;
  const styleLayers = map.getStyle().layers ?? [];
  const existingLayerIds = new Set(styleLayers.map((l) => l.id));

  // Performance optimization: only query enabled interactive and dynamic layers.
  const layers: string[] = [
    ...INTERACTIVE_LAYER_IDS,
    ...dynamicLayerIds,
  ].filter(
    (layerId) =>
      !disabledLayerIds.includes(layerId) && existingLayerIds.has(layerId),
  );

  if (!layers.length) return [];

  const features = map.queryRenderedFeatures(event.point, { layers }) ?? [];

  return features.filter((feature) => {
    const meta = feature.layer?.metadata as LayerMetadata | undefined;
    // If metadata interactive is not defined, assume the layer is interactive by default.
    // Some layers interactivity might be disabled via metadata (e.g. GeoEventsLayer)
    return typeof meta?.interactive === 'boolean' ? meta.interactive : true;
  });
};

/**
 * Compute which layer IDs should be excluded from interactive feature queries
 * based on feature flag / toggle / current route state.
 */
export const getDisabledLayerIds = ({
  showLocationV2,
  aqiStationsLayerEnabled,
  isModeratingPhotoSubmission,
}: {
  showLocationV2: boolean;
  aqiStationsLayerEnabled: boolean;
  /** Photo markers navigate to the moderation route, so they are not clickable while on it. */
  isModeratingPhotoSubmission: boolean;
}): string[] => {
  const disabledIds: string[] = [];
  if (!showLocationV2) {
    disabledIds.push(LOCATION_LABELS_LAYER_ID);
  }
  if (!aqiStationsLayerEnabled) {
    disabledIds.push(...INTERACTIVE_LAYER_IDS_BY_DRAWER_OR_POI.aqi);
  }
  if (isModeratingPhotoSubmission) {
    disabledIds.push(PHOTO_MARKERS_LAYER_ID);
  }
  return disabledIds;
};

/** Resolve which entity type a given layer id belongs to. */
export const getEntityTypeFromLayerId = (
  layerId: string,
): FeatureWithEntityType['entityType'] =>
  (LAYER_ID_TO_ENTITY_TYPE.get(layerId) as
    | FeatureWithEntityType['entityType']
    | undefined) ?? null;

// Attach derived entity type to each feature using its layer id.
export const attachEntityTypes = (
  features: MapGeoJSONFeature[],
  dynamicLayerIds: string[],
): FeatureWithEntityType[] => {
  return features.map((feature) => {
    if (dynamicLayerIds.includes(feature.layer!.id)) {
      return {
        feature,
        entityType:
          (feature.layer?.metadata as ExternalFeatureLayerMetadata)
            ?.cardDisplayType ?? null,
      };
    }
    return {
      feature,
      entityType: getEntityTypeFromLayerId(feature.layer!.id),
    };
  });
};

// Filter features by a whitelist of entity types.
export const filterFeaturesByEntityTypes = (
  features: FeatureWithEntityType[],
  entityTypes: string[],
): FeatureWithEntityType[] => {
  return features.filter(
    ({ entityType: featureEntityType }) =>
      featureEntityType && entityTypes.includes(featureEntityType),
  );
};

/**
 * Remove duplicate features that share the same source, feature.id, and entityType.
 * This can happen when a single map entity is rendered across multiple
 * interactive style layers backed by the same source.
 * Keeps the first occurrence of each unique (source, id, entityType) tuple.
 * Mapbox types source as optional even though rendered interactive features are
 * expected to have one. Features missing any tuple value are retained because they
 * cannot be safely deduplicated.
 */
export const deduplicateFeatures = (
  features: FeatureWithEntityType[],
): FeatureWithEntityType[] => {
  const seen = new Set<string>();
  return features.filter(({ feature, entityType }) => {
    const featureId = feature.id;
    const featureSource = feature.source;
    if (featureId == null || !entityType || featureSource == null) return true;
    const key = `${entityType}:${featureSource}:${featureId}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * Returns the listener layer ID for an external feature layer based on
 * its display style type and geometry type.
 */
export const getExternalFeatureLayerListenerLayerId = (
  featureLayer: ExternalFeatureLayer,
): string | null => {
  const { displayStyleData, geomType, mapLayerId } = featureLayer;

  if (displayStyleData.displayStyleType === 'embedded') {
    return displayStyleData.clickListenerLayerId;
  }

  if (displayStyleData.displayStyleType === 'simple_single_color') {
    return getSimpleStyleClickListenerLayerId(mapLayerId, geomType);
  }

  return null;
};

/**
 * Determines if a map click event should be ignored.
 * Clicks are ignored when:
 * 1. Click occurs inside a map dialog overlay
 * 2. Click lands on a pickable deck.gl object (e.g. aircraft), which handles its own click
 */
export const shouldIgnoreMapClick = (event: MapMouseEvent): boolean => {
  if (isDeckObjectAtMapClick(event)) return true;

  const dialogs = document.querySelectorAll('[role="dialog"]');
  if (!dialogs.length) return false;

  const { clientX, clientY } = event.originalEvent;

  // Convert NodeList to Array and use some() for early exit
  return Array.from(dialogs).some((dialog) => {
    const dialogRect = dialog.getBoundingClientRect();

    return (
      clientX >= dialogRect.left &&
      clientX <= dialogRect.right &&
      clientY >= dialogRect.top &&
      clientY <= dialogRect.bottom
    );
  });
};
